import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    document.title = 'About Negotrix - Commercial Intelligence & Negotiation Partner';
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  return (
    <div className={`min-h-screen bg-cipher-black text-white font-sans selection:bg-cipher-hypergreen selection:text-cipher-black transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-6 animate-slide-in-up">About Negotrix</h2>
          <h1 className="text-[8vw] leading-[0.95] font-black tracking-tighter mb-12 animate-slide-in-up" style={{animationDelay: '100ms'}}>
            Every vendor negotiates for their business.<br />
            <span className="text-cipher-hypergreen">You should have someone negotiating for yours.</span>
          </h1>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-cipher-dark">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="animate-fade-in-up">
              <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-6">What we do</h2>
              <h3 className="text-4xl lg:text-5xl font-black tracking-tight mb-8">Commercial intelligence and negotiation partner</h3>
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: '150ms'}}>
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                Negotrix is a commercial intelligence and negotiation partner that helps growing companies make better buying decisions across SaaS, cloud, AI, marketing, and enterprise vendors.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                We combine market intelligence, procurement expertise, and strategic negotiation to help businesses understand competitive pricing, negotiate with confidence, and build stronger vendor relationships.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                Our focus is not simply on reducing costs. We help organizations make informed commercial decisions that create long term value and support sustainable growth.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed mt-6 font-medium">
                Because better negotiations lead to better business outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="py-16 bg-cipher-black">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-6">Why We Exist</h2>
            <h3 className="text-4xl lg:text-5xl font-black tracking-tight max-w-4xl animate-fade-in-up">
              Growing businesses deserve the same commercial expertise as large enterprises.
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="animate-fade-in-up" style={{animationDelay: '100ms'}}>
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                While vendors negotiate contracts every day, most companies negotiate only a handful of significant agreements each year. This imbalance often leads to missed opportunities, less favorable commercial terms, and unnecessary spending.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                Negotrix was created to level the playing field.
              </p>
            </div>
            <div className="flex items-center animate-fade-in-up" style={{animationDelay: '200ms'}}>
              <div className="bg-cipher-dark p-8 lg:p-12 rounded-[2rem] border border-gray-800">
                <h4 className="text-2xl lg:text-3xl font-black text-white mb-4">Our mission is simple.</h4>
                <p className="text-xl text-cipher-hypergreen font-medium">To help businesses negotiate with clarity, confidence, and market intelligence.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 bg-cipher-dark">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-cipher-black p-8 lg:p-12 rounded-[2rem] border border-gray-800 animate-fade-in-up hover:border-cipher-hypergreen transition-all duration-300" style={{animationDelay: '100ms'}}>
              <div className="text-cipher-hypergreen text-sm tracking-widest uppercase font-bold mb-6">Vision</div>
              <h4 className="text-2xl lg:text-3xl font-black text-white mb-6">To become the trusted commercial partner for every growing business</h4>
              <p className="text-xl text-gray-300 leading-relaxed">
                By bringing transparency, expertise, and confidence to every vendor negotiation.
              </p>
            </div>

            <div className="bg-cipher-black p-8 lg:p-12 rounded-[2rem] border border-gray-800 animate-fade-in-up hover:border-cipher-hypergreen transition-all duration-300" style={{animationDelay: '200ms'}}>
              <div className="text-cipher-hypergreen text-sm tracking-widest uppercase font-bold mb-6">Mission</div>
              <h4 className="text-2xl lg:text-3xl font-black text-white mb-6">To help organizations make smarter commercial decisions</h4>
              <p className="text-xl text-gray-300 leading-relaxed">
                Through pricing intelligence, procurement expertise, and strategic negotiations that deliver long term business value.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-16 bg-cipher-black">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h2 className="text-4xl lg:text-5xl font-black tracking-tight mb-8">
                Better negotiations lead to better business outcomes.
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                Negotrix exists to help businesses make smarter commercial decisions through better market intelligence, stronger negotiations, and lasting vendor partnerships.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                Our goal is simple. Every agreement should create value, every negotiation should be informed, and every commercial decision should support long term growth.
              </p>
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: '200ms'}}>
              <div className="grid grid-cols-1 gap-6">
                <div className="flex items-start p-6 rounded-[2rem] border border-gray-800">
                  <span className="text-cipher-hypergreen text-3xl font-bold mr-4">01</span>
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2">Market Intelligence</h4>
                    <p className="text-gray-300">Understand competitive pricing and commercial benchmarks across your vendor categories.</p>
                  </div>
                </div>
                <div className="flex items-start p-6 rounded-[2rem] border border-gray-800">
                  <span className="text-cipher-hypergreen text-3xl font-bold mr-4">02</span>
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2">Strategic Negotiation</h4>
                    <p className="text-gray-300">Negotiate with confidence using proven frameworks and deep procurement expertise.</p>
                  </div>
                </div>
                <div className="flex items-start p-6 rounded-[2rem] border border-gray-800">
                  <span className="text-cipher-hypergreen text-3xl font-bold mr-4">03</span>
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2">Lasting Partnerships</h4>
                    <p className="text-gray-300">Build stronger vendor relationships that create long-term value for your business.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-cipher-dark">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-[6vw] leading-[0.95] font-black tracking-tighter text-white mb-8 animate-fade-in-up">
            Ready to level the playing field?<br />
            <span className="text-cipher-hypergreen">Start your commercial review.</span>
          </h2>
          <a href="/contact" className="inline-flex items-center justify-center px-8 py-4 text-sm tracking-wide font-bold uppercase rounded-full text-cipher-black bg-cipher-hypergreen hover:bg-white transition-all duration-300 animate-glow-pulse">
            Get in Touch
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
